import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileSignature, Upload } from "lucide-react";
import { useEsignSetup, createEsignRequest } from "@/components/esign/useEsignSetup";
import SignatorySelect from "@/components/esign/SignatorySelect";
import ESignRequestStatus, { ESignStatusBadge } from "@/components/esign/ESignRequestStatus";

const UploadCard = ({ signatories, onCreated }) => {
  const [sid, setSid] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const submit = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose a PDF first"); return; }
    setBusy(true);
    try {
      const d = await createEsignRequest({ blob: f, fileName: f.name, signatoryId: sid, docType: "upload", title: title || f.name });
      toast.success("eSign request created");
      fileRef.current.value = ""; setTitle("");
      onCreated(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4 rounded-sm space-y-3" data-testid="esign-upload-card">
      <div className="font-medium text-sm flex items-center gap-2"><Upload className="w-4 h-4" /> Sign any PDF</div>
      <div className="grid md:grid-cols-4 gap-2">
        <input ref={fileRef} type="file" accept="application/pdf" className="text-xs" data-testid="esign-upload-file-input" />
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="rounded-sm" data-testid="esign-upload-title-input" />
        <SignatorySelect signatories={signatories} value={sid} onChange={setSid} />
        <Button onClick={submit} disabled={!sid || busy} className="rounded-sm" data-testid="esign-upload-submit-btn">{busy ? "Uploading…" : "Send for eSign"}</Button>
      </div>
    </Card>
  );
};

export default function ESignDocuments() {
  const { config, signatories } = useEsignSetup();
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const load = useCallback(async () => {
    const r = await api.get("/esign/requests");
    setRows(r.data || []);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const onChange = (d) => { setActive(d); load(); };

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="esign-documents-page">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><FileSignature className="w-6 h-6" /> Aadhaar eSign</h1>
        <p className="text-sm text-muted-foreground">Sign invoices, POs, financial statements or any PDF with Aadhaar OTP. No USB token.</p>
      </div>
      {config && !config.configured && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-sm p-2" data-testid="esign-page-mock-banner">
          SANDBOX MOCK mode. Add Digio keys to switch to real Aadhaar eSign. Mock signatures are not legally valid.
        </div>
      )}
      <UploadCard signatories={signatories} onCreated={onChange} />
      <Card className="rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="esign-requests-table">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr><th className="text-left p-2">Document</th><th className="text-left p-2">Type</th><th className="text-left p-2">Signer</th><th className="text-left p-2">Created</th><th className="text-left p-2">Status</th><th className="p-2" /></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`esign-row-${r.id}`}>
                <td className="p-2">{r.title}</td>
                <td className="p-2 font-mono text-xs">{r.doc_type}</td>
                <td className="p-2">{r.signer_name}</td>
                <td className="p-2 text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-2"><ESignStatusBadge status={r.status} /></td>
                <td className="p-2 text-right"><Button size="sm" variant="outline" className="rounded-sm" onClick={() => setActive(r)} data-testid={`esign-row-open-${r.id}`}>Open</Button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-xs" data-testid="esign-empty">No eSign requests yet.</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg" data-testid="esign-row-dialog">
          <DialogHeader><DialogTitle>{active?.title}</DialogTitle></DialogHeader>
          {active && <ESignRequestStatus request={active} onChange={onChange} mockOtpHint={config?.mock_otp_hint} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
